/* eslint-disable react/jsx-no-useless-fragment */
/* eslint-disable no-nested-ternary */
/* eslint-disable indent */
import { yupResolver } from '@hookform/resolvers/yup';
import { Tooltip } from 'primereact/tooltip';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useFieldArray, useForm, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import './style.scss';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import PropTypes from 'prop-types';
import { useMutation } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import ReactRouterPrompt from 'react-router-prompt';

import { TEMPLATE, TOAST } from 'constant';
import MemberDialog from 'features/Council/components/AddMemberDialog';
import { updateCouncilValidationSchema } from 'features/Council/validation';
import { update as updateCouncil } from 'apis/councils.api';
import { Can } from 'components/Can';
import ErrorDialog from 'components/ErrorDialog';
import { DatePicker, FileInput, TextInput } from 'components/FormControl';
import { toast } from 'layout';
import {
  formatFileName,
  getAcadamicLevelShortHand,
  getContactOrganizationName,
  getEndingTimeOfDate,
  getNameRoleOfMemberInCouncil,
  getOrganizationNameById,
} from 'utils/func';
import DateTimePicker from 'components/FormControl/DateTimePicker';
import ConfirmSendMailDialog from '../../ConfirmSendMailDialog';
import ConfirmDeleteMemberDialog from '../../ConfirmDeleteMemberDialog';
import ConfirmCloseCouncilDialog from '../../ConfirmCloseCouncilDialog';

const formOptions = { resolver: yupResolver(updateCouncilValidationSchema) };
// eslint-disable-next-line import/prefer-default-export
export function EditCouncil({
  organizations,
  owner,
  generalInfoProjectProposal,
  roles,
  handleDownloadFile,
  council,
  refetch,
  projectProposalMembers,
  previousVersion,
}) {
  // #region Data
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { projectProposalId, councilTypeId, councilId } = useParams();
  const [isVisibleErrorDialog, setIsVisibleDialogError] = useState(false);
  const [isVisibleConfirmDoneDialog, setIsVisibleConfirmDoneDialog] = useState(false);
  const [isVisibleConfirmDeleteMemberDialog, setIsVisibleConfirmDeleteMemberDialog] =
    useState(false);
  const [dataUpdateCouncil, setDataUpdateCouncil] = useState({});
  const [isSentMail, setIsSentMail] = useState(false);
  const [isCloseCouncil, setIsCloseCouncil] = useState([false, true]);

  const MemberRef = useRef();
  const { mutate, error: errorRes, isError: isErrorRes } = useMutation(updateCouncil);
  const formDefaultValues = {
    name: '',
    establishDate: '',
    membersFields: [],
  };

  const isCombineSpecializationAndFinance = useMemo(
    () => previousVersion?.template === TEMPLATE[1595],
    [previousVersion]
  );

  const {
    handleSubmit,
    control,
    setValue,
    watch,
    reset,
    formState: { errors, isValid },
  } = useForm({ mode: 'onChange', defaultValues: formDefaultValues, ...formOptions });

  const {
    fields: membersFields,
    append: appendMembers,
    remove: removeMembers,
    update: updateMembers,
  } = useFieldArray({
    control,
    reset,
    name: 'councilMembers',
  });

  const { isDirty, dirtyFields } = useFormState({
    control,
  });

  const decisionToEstablishFileUrl = watch('decisionToEstablishFileUrl');
  const getPageMargins = () => '@page { margin: 0.5in 0.5in 0.2in 0.2in  !important; }';
  // #endregion

  // #region Events
  const onSubmit = async (data) => {
    const formData = new FormData();

    if (data.decisionToEstablish) {
      formData.append('establishmentFile', data.decisionToEstablish);
    }
    if (data?.establishDate) {
      formData.append('timeCreated', getEndingTimeOfDate(new Date(data.establishDate)));
    }
    if (data?.reviewDeadline) {
      formData.append('reviewDeadline', getEndingTimeOfDate(new Date(data.reviewDeadline)));
    }

    if (data?.timeMet) {
      formData.append('timeMet', new Date(data.timeMet));
    }
    if (data?.meetingLocation) {
      formData.append('meetingLocation', data.meetingLocation);
    }
    if (data?.contact) {
      formData.append('contact', data.contact);
    }
    if (data?.documentUrl) {
      formData.append('documentUrl', data.documentUrl);
    }

    formData.append('members', JSON.stringify(membersFields));
    formData.append('projectProposalId', previousVersion?.id);
    formData.append('councilTypeId', councilTypeId);
    formData.append('councilId', councilId);
    formData.append('name', data.name);

    mutate(formData, {
      onSuccess: (res) => {
        toast(TOAST.SUCCESS, t('council.edit.success'));
        const currentValues = watch();
        refetch();
        setIsSentMail(false);
        if (data.decisionToEstablish && res?.data?.establishmentFileUrl) {
          currentValues.decisionToEstablishFileUrl = res?.data?.establishmentFileUrl;
        }
        reset({
          ...currentValues,
          decisionToEstablish: null,
        });
      },
      onError: (res) => {
        if (res?.response?.data?.message === 'Error delete old members') {
          setDataUpdateCouncil(formData);
          setIsVisibleConfirmDeleteMemberDialog(true);
        } else {
          setIsVisibleDialogError(true);
        }
      },
    });
  };
  // Set value into form fields
  useEffect(() => {
    setValue('establishDate', new Date(council?.timeCreated));
    setValue(
      'reviewDeadline',
      council?.projectProposalCouncils?.[0]?.reviewDeadline
        ? new Date(council?.projectProposalCouncils?.[0]?.reviewDeadline)
        : ''
    );
    if (council?.timeMet) {
      setValue('timeMet', new Date(council?.timeMet));
    }
    setValue('meetingLocation', council?.meetingLocation);
    setValue('contact', council?.contact);
    setValue('documentUrl', council?.documentUrl);
    setValue('name', council?.name);
    setValue(
      'councilMembers',
      council?.councilMembers?.map((member) => ({
        academicDegree: member?.contact?.scientificProfile?.academicDegree,
        academicRank: member?.contact?.scientificProfile?.academicRank,
        contact: member?.contact,
        email: member?.contact?.email,
        fullname: member?.contact?.fullname,
        id: member?.id,
        organizationId: member?.contact?.organizationId,
        roleId: member?.councilMemberRole?.id,
        specificOrganizationName: member?.contact?.specificOrganizationName,
      }))
    );

    setValue(
      'decisionToEstablishFileUrl',
      council?.projectProposalCouncils?.[0]?.establishmentFileUrl
    );
  }, [council]);

  const checkValidation = () => {
    if (!isValid) {
      toast(TOAST.ERROR, t('errorMessage.validationErrorMessage'));
    }
  };

  const footerConfirmationPrompt = (onConfirm, onCancel) => (
    <div>
      <Button
        label={t('formLayout.action.cancel')}
        className="w-7rem"
        severity="danger"
        onClick={onCancel}
        autoFocus
      />
      <Button
        label={t('formLayout.action.ok')}
        className="w-7rem"
        severity="info"
        onClick={onConfirm}
      />
    </div>
  );

  const handleCloseCouncil = () => {
    if (council && !council?.completedAt) {
      setIsCloseCouncil([true, false]);
    } else {
      navigate(-1);
    }
  };
  // #endregion

  return (
    <>
      {isErrorRes && (
        <ErrorDialog
          title={t('council.edit.titleEditCouncilError')}
          isError={isErrorRes}
          error={errorRes}
          visible={isVisibleErrorDialog}
          setVisible={setIsVisibleDialogError}
        />
      )}
      <ConfirmSendMailDialog
        visible={isVisibleConfirmDoneDialog}
        setVisible={setIsVisibleConfirmDoneDialog}
        council={council}
        reference={projectProposalId}
        setIsSentMail={setIsSentMail}
        refetch={refetch}
      />
      <ConfirmDeleteMemberDialog
        visible={isVisibleConfirmDeleteMemberDialog}
        setVisible={setIsVisibleConfirmDeleteMemberDialog}
        data={dataUpdateCouncil}
        reference={projectProposalId}
        reset={reset}
        watch={watch}
        setIsSentMail={setIsSentMail}
        refetch={refetch}
      />
      <ReactRouterPrompt
        when={
          (isDirty && Object.keys(dirtyFields).length) ||
          (council && !council?.completedAt && isCloseCouncil[1])
        }
      >
        {({ isActive, onConfirm, onCancel }) => (
          <>
            {council && !council?.completedAt ? (
              <ConfirmCloseCouncilDialog
                visible={isActive}
                setVisible={setIsCloseCouncil}
                onConfirm={onConfirm}
                onHide={onCancel}
              />
            ) : (
              <Dialog
                visible={isActive}
                header={<p className="text-blue-500">{t('projectProposal.confirmationTitle')}</p>}
                position="center"
                style={{ width: '300px' }}
                onHide={onCancel}
                draggable={false}
                footer={() => footerConfirmationPrompt(onConfirm, onCancel)}
              >
                <p className="text-lg py-3">{t('projectProposal.confirmationPromptMessage')}</p>
              </Dialog>
            )}
          </>
        )}
      </ReactRouterPrompt>
      <div className="card relative detail-info-project-proposal">
        <style>{getPageMargins()}</style>

        <h3 className="mb-5 line-height-3 text-4xl text-center font-bold">
          {t('council.viewList.update')}{' '}
          {t(
            `council.viewList.${council?.councilType?.code}${
              isCombineSpecializationAndFinance ? 'AndFinance' : ''
            }`
          )}
        </h3>

        <h4>{t('projectProposal.general.viewLabelCouncil')}</h4>

        <div className="ml-5 text-lg">
          <div className="flex mb-1">
            <div className="mr-2 font-semibold">
              {t('projectProposal.general.projectNameAndType.vietnameseName')}:{' '}
            </div>
            <div className="flex-1">{generalInfoProjectProposal?.vietnameseName}</div>
          </div>
          <div className="flex mb-1">
            <div className="mr-2 font-semibold">
              {t('projectProposal.general.projectNameAndType.englishName')}:{' '}
            </div>
            <div className="flex-1">{generalInfoProjectProposal?.englishName}</div>
          </div>
          <div className="flex mb-1">
            <div className="mr-2 font-semibold">
              {t('projectProposal.general.ownerSection.labelCouncil')}:{' '}
            </div>
            <div className="flex-1">
              {`${getAcadamicLevelShortHand(owner?.academicRank, owner?.academicDegree, t, true)}${
                owner?.fullname ?? generalInfoProjectProposal?.owner?.fullname ?? ''
              }`}
            </div>
          </div>
        </div>

        <h4 className="mt-4">
          {t(
            `council.edit.label.${council?.councilType?.code}${
              isCombineSpecializationAndFinance ? 'AndFinance' : ''
            }`
          )}
        </h4>

        <form className="p-fluid ml-5" onSubmit={handleSubmit(onSubmit)}>
          <div className="relative">
            <div>
              <TextInput
                label={t(`council.fields.name.${council?.councilType?.code}`)}
                name="name"
                isFontWeightLabel
                isRequired
                control={control}
                errors={errors}
              />
            </div>
            <div className="flex flex-wrap ">
              <div className="w-3">
                <DatePicker
                  label={t('council.fields.establishDate')}
                  isRequired
                  name="establishDate"
                  isFontWeightLabel
                  control={control}
                  errors={errors}
                />
              </div>
              <div className="w-3 ml-8">
                <DatePicker
                  label={t('council.fields.reviewDeadline')}
                  isRequired
                  name="reviewDeadline"
                  isFontWeightLabel
                  control={control}
                  errors={errors}
                />
              </div>
              <div className="w-3 ml-8">
                <DateTimePicker
                  label={t('council.createForManyProjectProposal.infoCouncil.timeMet')}
                  name="timeMet"
                  isFontWeightLabel
                  control={control}
                  errors={errors}
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-6">
              <div className="w-12">
                <TextInput
                  label={t('council.createForManyProjectProposal.infoCouncil.meetingLocation')}
                  name="meetingLocation"
                  isFontWeightLabel
                  control={control}
                  errors={errors}
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-6">
              <div className="w-12">
                <TextInput
                  label={t('council.createForManyProjectProposal.infoCouncil.contact')}
                  name="contact"
                  isFontWeightLabel
                  control={control}
                  errors={errors}
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-6">
              <div className="w-12">
                <TextInput
                  label={t('council.createForManyProjectProposal.infoCouncil.documentUrl')}
                  name="documentUrl"
                  isFontWeightLabel
                  control={control}
                  errors={errors}
                />
              </div>
            </div>

            <div>
              <FileInput
                label={t('council.fields.decisionToEstablish')}
                isFontWeightLabel
                name="decisionToEstablish"
                accept=".pdf"
                acceptOnly="pdfOnly"
                control={control}
                errors={errors}
                isRequired
              />
              {decisionToEstablishFileUrl && (
                <span
                  className="p-button p-button-text p-button-sm mb-3"
                  onClick={() => handleDownloadFile(decisionToEstablishFileUrl, councilId)}
                >
                  <i className="pi pi-file-pdf text-red-500 text-xl" />{' '}
                  <span className="text-blue-500 underline font-italic text-base">
                    {formatFileName(decisionToEstablishFileUrl)}
                  </span>
                </span>
              )}
            </div>
          </div>

          <div className="mt-5 mb-3" style={{ fontWeight: 900, fontSize: '20px' }}>
            {t('council.create.titleContent')}
          </div>
          <div className="form-table">
            <table className="mb-2">
              <thead>
                <tr>
                  <th className="text-center" style={{ minWidth: 50, width: 50 }}>
                    {t('formLayout.numericalOrder')}
                  </th>
                  <th className="text-center" style={{ minWidth: 200, width: '30%' }}>
                    {`${t('council.member.fields.academicRank')}, ${t(
                      'council.member.fields.academicDegree'
                    )}, ${t('council.member.fields.name')}`}
                  </th>
                  <th className="text-center" style={{ minWidth: 200, width: '33%' }}>
                    Email
                  </th>
                  <th className="text-center" style={{ minWidth: 200, width: '33%' }}>
                    {t('council.member.fields.organization')}
                  </th>
                  <th className="text-center" style={{ minWidth: 200, width: '33%' }}>
                    {t('council.member.fields.role')}
                  </th>
                  <th className="float text-center" style={{ minWidth: 80, width: 80 }}>
                    {t('council.member.fields.action')}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr className="font-bold">
                  <td colSpan={6}>
                    <div>
                      <span>{t('council.create.add')}</span>
                      <i
                        onClick={() => {
                          MemberRef.current.open({
                            append: appendMembers,
                            type: 'APPEND',
                          });
                        }}
                        className="add-scientific-secretary pi pi-plus text-primary cursor-pointer bg-primary p-1 border-circle ml-1"
                        data-pr-tooltip={t('formLayout.action.add')}
                        data-pr-position="left"
                      />
                      <Tooltip target=".add-scientific-secretary" />
                    </div>
                  </td>
                </tr>
                {membersFields.map((member, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <tr key={index}>
                    <td className="text-center">{index + 1}</td>
                    <td>
                      {`${getAcadamicLevelShortHand(
                        member?.academicRank,
                        member?.academicDegree,
                        t,
                        true
                      )}${member?.fullname}`}
                    </td>
                    <td>{member?.email}</td>
                    <td>
                      {typeof value === 'object'
                        ? getContactOrganizationName(organizations, member?.contact, t)
                        : getOrganizationNameById(
                            member?.organizationId,
                            organizations,
                            member?.specificOrganizationName,
                            t
                          )}
                    </td>
                    <td>{getNameRoleOfMemberInCouncil(roles, member?.roleId)}</td>

                    <td className="flex flex-column float text-center p-0 border-none border-bottom-1">
                      <i
                        onClick={() => {
                          MemberRef.current.open({
                            update: updateMembers,
                            index,
                            value: member,
                            type: 'UPDATE',
                          });
                        }}
                        className="edit-icon pi pi-pencil bg-red text-primary cursor-pointer my-2"
                        data-pr-tooltip={t('formLayout.action.edit')}
                        data-pr-position="left"
                      />
                      <Tooltip target=".edit-icon" />

                      <i
                        onClick={() => removeMembers(index)}
                        className="remove-icon pi pi-times text-red-700 cursor-pointer mb-2"
                        data-pr-tooltip={t('formLayout.action.remove')}
                        data-pr-position="left"
                      />
                      <Tooltip target=".remove-icon" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap justify-content-end mt-8">
            <Button
              label={t('formLayout.action.close')}
              severity="danger"
              type="button"
              onClick={handleCloseCouncil}
              className="mr-2 w-7rem"
            />
            <Button
              label={t('formLayout.action.save')}
              severity="info"
              type="submit"
              onClick={checkValidation}
              disabled={!(isDirty && Object.keys(dirtyFields).length)}
              className="mr-2 w-7rem"
            />
            <Can do="send_mail" on="council">
              <Button
                label={
                  council?.completedAt
                    ? council?.isSentMailAllMembers
                      ? t('formLayout.action.doneAndSendMail')
                      : t('formLayout.action.sentEmailToMembers')
                    : t('formLayout.action.doneAndSendMail')
                }
                severity="success"
                type="button"
                onClick={() => setIsVisibleConfirmDoneDialog(true)}
                disabled={
                  (isDirty && Object.keys(dirtyFields).length) || isSentMail || council?.completedAt
                    ? council?.isSentMailAllMembers
                    : council?.completedAt
                }
                className="mr-2 w-auto"
              />
            </Can>
          </div>
          <MemberDialog
            ref={MemberRef}
            organizations={organizations}
            curMembers={membersFields}
            roles={roles}
            projectProposalMembers={projectProposalMembers}
          />
          <ConfirmCloseCouncilDialog visible={isCloseCouncil[0]} setVisible={setIsCloseCouncil} />
        </form>
      </div>
    </>
  );
}

EditCouncil.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  organizations: PropTypes.array,
  // eslint-disable-next-line react/forbid-prop-types
  owner: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  roles: PropTypes.array,
  // eslint-disable-next-line react/forbid-prop-types
  generalInfoProjectProposal: PropTypes.object,
  handleDownloadFile: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  council: PropTypes.object,
  refetch: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  projectProposalMembers: PropTypes.array,
  // eslint-disable-next-line react/forbid-prop-types
  previousVersion: PropTypes.object,
};

EditCouncil.defaultProps = {
  projectProposalMembers: [],
  organizations: [],
  roles: [],
  generalInfoProjectProposal: {},
  council: {},
  previousVersion: {},
};
